import { Box, FormControl, FormControlProps, InputLabel, InputLabelProps, MenuItem, Select as MuiSelect, OutlinedInput, SelectChangeEvent, Typography } from "@mui/material";
import React, { FC, ReactNode, useCallback } from "react";
import { InputSelectProps } from "./types/FieldTypes";

export interface ISelectItem<TId = any> {
    id?: TId;
    name?: string | ReactNode;
    description?: string;
}

export interface ISelectProps<TId = any> extends Omit<InputSelectProps<TId>, 'margin' | 'onChange'> {
    nullOption?: boolean;
    label?: string;
    FormControlProps?: FormControlProps;
    onChange?: (event: SelectChangeEvent<any>, child: React.ReactNode, selectedItem: any) => void;
    options?: ISelectItem<TId>[];
    helperText?: ReactNode;
    margin: FormControlProps['margin'];
    InputLabelProps?: InputLabelProps;
}

const InputDropdownField: FC<ISelectProps> = ({
    FieldInputProps: { state: { propertyPath, value }, actions: { update, blur, focus, touch } },
    label,
    onChange,
    fullWidth,
    sx,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    FormControlProps,
    InputLabelProps,
    margin,
    nullOption,
    helperText,
    options,
    ...rest
}) => {
    const onChangeCombined = useCallback((event: SelectChangeEvent<{ name: string | undefined; value: number; }>, child: React.ReactNode) => {
        const selectedItem = options?.find(x => x.id?.toString() === event.target.value?.toString());
        
        if (onChange) {
            onChange(event, child, selectedItem);
        }

        const newValue = selectedItem?.id ?? undefined;
        
        update(newValue, true, true, true);
    }, [onChange, update, options]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    return (
        <FormControl
            key={`${propertyPath}-control`}
            {...FormControlProps}
            margin={margin}
            fullWidth={FormControlProps?.fullWidth === undefined ? fullWidth : FormControlProps?.fullWidth}
            sx={{ ...FormControlProps?.sx }}
        >
            <InputLabel {...InputLabelProps} id={`${propertyPath}-label`}>{label}</InputLabel>
            <MuiSelect
                {...rest as any}
                labelId={`${propertyPath}-label`}
                inputProps={{
                    ...inputProps,
                    onBlur: onBlurCombined,
                    onFocus: onFocusCombined
                }}
                input={InputLabelProps?.shrink ? <OutlinedInput notched label={label} disabled={rest.disabled} />  : undefined}
                fullWidth={FormControlProps?.fullWidth === undefined ? fullWidth : FormControlProps?.fullWidth}
                label={label}
                value={value ?? ''}
                renderValue={
                    rest.renderValue ?? ((id?: any) => {
                        const item = options?.find(x => x.id?.toString() === id?.toString());

                        if (!item) {
                            return null;
                        }
                        
                        return (
                            <Box 
                                sx={{
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    width: '100%'
                                }}
                            >
                                {item.name}
                                {item.description && (
                                    <Typography variant="caption">
                                        {item.description}
                                    </Typography>
                                )}
                            </Box>
                        )
                    })
                }
                onChange={onChangeCombined}
            >
                {
                    (nullOption !== false) && (
                        <MenuItem
                            value={''}
                        >
                            &nbsp;
                        </MenuItem>
                    )
                }
                {
                    options?.map(item => {
                        return (
                            <MenuItem
                                key={item.id?.toString()}
                                value={item.id?.toString()}
                            >
                                <Box 
                                    sx={{
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        width: '100%'
                                    }}
                                >
                                    {item.name}
                                    {item.description && (
                                        <Typography variant="caption">
                                            {item.description}
                                        </Typography>
                                    )}
                                </Box>
                            </MenuItem>
                        );
                    })
                }
            </MuiSelect>
        </FormControl>
    );
}

export default InputDropdownField